import React, { useState } from 'react';
import { Calculator, Euro, Plane, Car, Bus, Hotel, UtensilsCrossed, AlertCircle } from 'lucide-react';
import { travelCalculatorDefaults } from '../data/mockData';
import './TravelCalculator.css';

function TravelCalculator() {
  const [selectedCountry, setSelectedCountry] = useState('thailand');
  const [days, setDays] = useState(14);
  const [transportMode, setTransportMode] = useState('publicTransport');
  const [accommodationLevel, setAccommodationLevel] = useState('mid');
  
  const defaults = travelCalculatorDefaults[selectedCountry] || travelCalculatorDefaults.thailand;
  
  const [customValues, setCustomValues] = useState({
    flightPrice: defaults.avgFlightPrice,
    parking: defaults.parking,
    publicTransportToAirport: defaults.publicTransportToAirport,
    publicTransportFromAirport: defaults.publicTransportFromAirport,
    fuelCost: defaults.carCosts.fuel,
    tolls: defaults.carCosts.tolls,
    tunnel: defaults.carCosts.tunnel,
    accommodation: defaults.accommodation[accommodationLevel],
    dailyTransport: defaults.dailyTransport,
    food: defaults.food
  });

  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    const newDefaults = travelCalculatorDefaults[country];
    setCustomValues({
      flightPrice: newDefaults.avgFlightPrice,
      parking: newDefaults.parking,
      publicTransportToAirport: newDefaults.publicTransportToAirport,
      publicTransportFromAirport: newDefaults.publicTransportFromAirport,
      fuelCost: newDefaults.carCosts.fuel,
      tolls: newDefaults.carCosts.tolls,
      tunnel: newDefaults.carCosts.tunnel,
      accommodation: newDefaults.accommodation[accommodationLevel],
      dailyTransport: newDefaults.dailyTransport,
      food: newDefaults.food
    });
  };

  const handleAccommodationChange = (level) => {
    setAccommodationLevel(level);
    setCustomValues(prev => ({
      ...prev,
      accommodation: defaults.accommodation[level]
    }));
  };
  
  const updateCustomValue = (field, value) => {
    setCustomValues(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  const calculateTotal = () => {
    let total = customValues.flightPrice;
    
    if (transportMode === 'car') {
      total += customValues.parking * Math.ceil(days / 7);
      total += customValues.fuelCost + customValues.tolls + customValues.tunnel;
    } else {
      total += customValues.publicTransportToAirport;
    }
    
    total += customValues.publicTransportFromAirport;
    total += customValues.accommodation * days;
    total += customValues.dailyTransport * days;
    total += customValues.food * days;
    
    return total;
  };

  const total = calculateTotal();
  const perDay = (total / days).toFixed(2);

  return (
    <section id="calculator" className="section calculator-section">
      <div className="calculator-header">
        <h2 className="section-title">Reisekalkulator</h2>
        <p className="section-subtitle">
          Berechne deine Reisekosten basierend auf aktuellen Durchschnittspreisen 2025
        </p>
      </div>

      <div className="calculator-container card glass">
        <div className="calculator-inputs">
          <div className="input-group">
            <label className="input-label">
              <Plane size={18} />
              Reiseziel
            </label>
            <select 
              className="select-input"
              value={selectedCountry}
              onChange={(e) => handleCountryChange(e.target.value)}
            >
              <option value="germany">Deutschland</option>
              <option value="austria">Österreich</option>
              <option value="liechtenstein">Liechtenstein</option>
              <option value="switzerland">Schweiz</option>
              <option value="italy">Italien</option>
              <option value="hungary">Ungarn</option>
              <option value="czechia">Tschechien</option>
              <option value="croatia">Kroatien</option>
              <option value="bosnia">Bosnien und Herzegowina</option>
              <option value="tenerife">Teneriffa</option>
              <option value="elhierro">El Hierro</option>
              <option value="poland">Polen</option>
              <option value="portugal">Portugal</option>
              <option value="jordan">Jordanien</option>
              <option value="oman">Oman</option>
              <option value="dubai">Dubai</option>
              <option value="philippines">Philippinen</option>
              <option value="thailand">Thailand</option>
              <option value="hongkong">Hong Kong</option>
              <option value="india">Indien</option>
              <option value="taiwan">Taiwan</option>
              <option value="newyork">New York</option>
            </select>
          </div>

          <div className="input-group">
            <label className="input-label">
              <Calculator size={18} />
              Reisedauer (Tage)
            </label>
            <input 
              type="number" 
              className="text-input"
              value={days}
              onChange={(e) => setDays(Math.max(1, parseInt(e.target.value) || 1))}
              min="1"
            />
          </div>

          <div className="input-group">
            <label className="input-label">
              <Car size={18} />
              Anreise zum Flughafen
            </label>
            <div className="radio-group">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="transport"
                  value="publicTransport"
                  checked={transportMode === 'publicTransport'}
                  onChange={(e) => setTransportMode(e.target.value)}
                />
                <span>Öffentliche Verkehrsmittel</span>
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="transport"
                  value="car"
                  checked={transportMode === 'car'}
                  onChange={(e) => setTransportMode(e.target.value)}
                />
                <span>Eigenes Auto (mit Parkgebühren)</span>
              </label>
            </div>
          </div>

          <div className="input-group">
            <label className="input-label">
              <Hotel size={18} />
              Unterkunftsniveau
            </label>
            <div className="button-group">
              <button 
                className={`level-btn ${accommodationLevel === 'low' ? 'active' : ''}`}
                onClick={() => handleAccommodationChange('low')}
              >
                Budget
              </button>
              <button 
                className={`level-btn ${accommodationLevel === 'mid' ? 'active' : ''}`}
                onClick={() => handleAccommodationChange('mid')}
              >
                Mittel
              </button>
              <button 
                className={`level-btn ${accommodationLevel === 'high' ? 'active' : ''}`}
                onClick={() => handleAccommodationChange('high')}
              >
                Komfort
              </button>
            </div>
          </div>

          <div className="input-group">
            <label className="input-label">
              <Euro size={18} />
              Flugpreis (€) - Ø {defaults.avgFlightPrice}€
            </label>
            <input 
              type="number" 
              className="text-input"
              value={customValues.flightPrice}
              onChange={(e) => updateCustomValue('flightPrice', e.target.value)}
            />
          </div>
        </div>

        <div className="calculator-breakdown">
          <h3 className="breakdown-title">Kostenaufstellung</h3>
          
          <div className="breakdown-item">
            <span>Flug</span>
            <input 
              type="number" 
              className="breakdown-input"
              value={customValues.flightPrice}
              onChange={(e) => updateCustomValue('flightPrice', e.target.value)}
            />
          </div>

          {transportMode === 'car' ? (
            <>
              <div className="breakdown-item">
                <span>Parkgebühren ({Math.ceil(days / 7)} Wochen)</span>
                <input 
                  type="number" 
                  className="breakdown-input"
                  value={customValues.parking}
                  onChange={(e) => updateCustomValue('parking', e.target.value)}
                />
              </div>
              <div className="breakdown-item">
                <span>Benzin</span>
                <input 
                  type="number" 
                  className="breakdown-input"
                  value={customValues.fuelCost}
                  onChange={(e) => updateCustomValue('fuelCost', e.target.value)}
                />
              </div>
              <div className="breakdown-item">
                <span>Maut</span>
                <input 
                  type="number" 
                  className="breakdown-input"
                  value={customValues.tolls}
                  onChange={(e) => updateCustomValue('tolls', e.target.value)}
                />
              </div>
              <div className="breakdown-item">
                <span>Tunnel</span>
                <input 
                  type="number" 
                  className="breakdown-input"
                  value={customValues.tunnel}
                  onChange={(e) => updateCustomValue('tunnel', e.target.value)}
                />
              </div>
            </>
          ) : (
            <div className="breakdown-item">
              <span>ÖPNV zum Flughafen</span>
              <input 
                type="number" 
                className="breakdown-input"
                value={customValues.publicTransportToAirport}
                onChange={(e) => updateCustomValue('publicTransportToAirport', e.target.value)}
              />
            </div>
          )}

          <div className="breakdown-item">
            <span>ÖPNV vom Zielflughafen</span>
            <input 
              type="number" 
              className="breakdown-input"
              value={customValues.publicTransportFromAirport}
              onChange={(e) => updateCustomValue('publicTransportFromAirport', e.target.value)}
            />
          </div>

          <div className="breakdown-item">
            <span>Unterkunft pro Nacht</span>
            <input 
              type="number" 
              className="breakdown-input"
              value={customValues.accommodation}
              onChange={(e) => updateCustomValue('accommodation', e.target.value)}
            />
          </div>

          <div className="breakdown-item">
            <span>Tägliche Fortbewegung</span>
            <input 
              type="number" 
              className="breakdown-input"
              value={customValues.dailyTransport}
              onChange={(e) => updateCustomValue('dailyTransport', e.target.value)}
            />
          </div>

          <div className="breakdown-item">
            <span>Essen & Getränke pro Tag</span>
            <input 
              type="number" 
              className="breakdown-input"
              value={customValues.food}
              onChange={(e) => updateCustomValue('food', e.target.value)}
            />
          </div>

          <div className="breakdown-divider"></div>

          <div className="breakdown-total">
            <div>
              <div className="total-label">Gesamtkosten</div>
              <div className="total-days">{days} Tage</div>
            </div>
            <div className="total-amount">€{total.toFixed(2)}</div>
          </div>

          <div className="per-day-cost">
            <AlertCircle size={16} />
            <span>Ø €{perDay} pro Tag</span>
          </div>
        </div>
      </div>

      <div className="calculator-note">
        <AlertCircle size={20} />
        <p>
          Alle Werte basieren auf Durchschnittskosten 2025 und sind anpassbar. 
          Nutze die Felder, um deine eigenen Preise einzutragen.
        </p>
      </div>
    </section>
  );
}

export default TravelCalculator;
